'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const PLAN = {
  id: 'pro',
  name: 'NHL Analytics Pro',
  price: 29,
  color: 'text-primary',
  border: 'border-primary/40',
  features: [
    'Projections ML illimitées',
    'Live Analytics NHL',
    'ML Engine complet',
    'Alertes avancées (6 types)',
    'API illimitée',
    'Export CSV/JSON',
    'Analytics Copilot (GPT-4)',
    'Monte Carlo avancé (50k iter.)',
    'SimulationLab complet',
    'Player Intelligence',
    'Market Intelligence',
    'Event Detection',
    'Webhooks temps réel',
    'Custom AI Models',
    'Quant Terminal',
    'Données brutes NHL',
    'SLA 99.9% garanti',
    'Support prioritaire 24h',
  ],
};

const STATS = [
  { value: '5,000+', label: 'Analystes actifs' },
  { value: '75.3%', label: 'Précision ML' },
  { value: '99.2%', label: 'Uptime données' },
  { value: '2.1M+', label: 'Projections générées' },
];

const TESTIMONIALS = [
  { name: 'Marc D.', role: 'Analyste sportif', text: 'La précision des projections est impressionnante. +12% ROI sur 3 mois.', rating: 5 },
  { name: 'Sophie L.', role: 'Quant researcher', text: 'Le terminal Bloomberg-style est exactement ce dont j\'avait besoin.', rating: 5 },
  { name: 'Alex T.', role: 'Fantasy NHL Pro', text: 'Les alertes gardiens en temps réel ont transformé ma stratégie.', rating: 5 },
];

const FEATURES = [
  { icon: 'SparklesIcon', title: 'IA Prédictive', desc: 'Modèles ML entraînés sur 5 saisons NHL. Précision 75.3% validée.' },
  { icon: 'BoltIcon', title: 'Données Live', desc: 'NHL en temps réel. Blessures, alignements, cotes — 30s de délai max.' },
  { icon: 'BeakerIcon', title: 'SimulationLab', desc: 'Monte Carlo 50,000 itérations. Distributions probabilistes complètes.' },
  { icon: 'CpuChipIcon', title: 'AI Agents', desc: '6 agents autonomes surveillent la NHL 24/7 et génèrent des alertes.' },
  { icon: 'ChartBarIcon', title: 'Analytics Pro', desc: 'Viz Engine, Knowledge Graph, Market Intelligence — niveau institutionnel.' },
  { icon: 'ShieldCheckIcon', title: 'Transparence', desc: 'Dashboard Trust & Transparency. Calibration et limites documentées.' },
];

export default function LaunchClient() {
  const [email, setEmail] = useState('');
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [referralCode] = useState('LAUNCH2025');
  const [copied, setCopied] = useState(false);

  const handleWaitlist = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) setWaitlistSubmitted(true);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(referralCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="text-sm font-semibold text-foreground">NHL Analytics Pro</span>
            <span className="text-2xs bg-primary/10 text-primary px-2 py-0.5 rounded font-mono-data">LAUNCH</span>
          </div>
          <div className="flex items-center gap-3">
            <Link href="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Dashboard</Link>
            <Link href="/onboarding" className="px-4 py-1.5 bg-primary text-white text-sm rounded-lg font-medium hover:bg-primary/90 transition-colors">
              Essai gratuit 1 mois
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 py-20 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-positive pulse-dot" />
          <span className="text-xs text-primary font-medium">Lancement officiel — NHL 2025–26</span>
        </div>
        <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
          Intelligence Sportive<br />
          <span className="text-primary">de Nouvelle Génération</span>
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
          La plateforme analytique NHL alimentée par IA utilisée par 5,000+ analystes.
          Projections ML, simulations Monte Carlo, données live et terminal quantitatif.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link href="/onboarding" className="px-8 py-3.5 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors text-sm">
            Commencer — 1 mois gratuit →
          </Link>
          <Link href="/trust-dashboard" className="px-8 py-3.5 border border-border rounded-xl font-medium text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors text-sm">
            Voir la précision →
          </Link>
        </div>
        <p className="text-xs text-muted-foreground mt-4">30 jours gratuits · Puis $29/mois · Annulation à tout moment</p>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16">
          {STATS.map((s) => (
            <div key={s.label} className="p-5 rounded-xl bg-card border border-border">
              <div className="text-2xl font-bold text-primary font-mono-data">{s.value}</div>
              <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="text-2xl font-bold text-center text-foreground mb-10">Tout ce dont vous avez besoin</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="p-6 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <Icon name={f.icon as any} size={20} className="text-primary" />
              </div>
              <h3 className="text-sm font-semibold text-foreground mb-2">{f.title}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-6xl mx-auto px-4 py-16" id="pricing">
        <h2 className="text-2xl font-bold text-center text-foreground mb-3">Un seul tarif. Tout inclus.</h2>
        <p className="text-sm text-muted-foreground text-center mb-10">1 mois gratuit à l'inscription · Puis $29/mois · Annulation à tout moment</p>

        {/* Trial Banner */}
        <div className="max-w-md mx-auto mb-6">
          <div className="flex items-center gap-3 p-4 rounded-xl bg-positive/10 border border-positive/30">
            <div className="w-10 h-10 rounded-full bg-positive/20 flex items-center justify-center flex-shrink-0">
              <Icon name="GiftIcon" size={20} className="text-positive" />
            </div>
            <div>
              <div className="text-sm font-semibold text-foreground">Offre de bienvenue — 1 mois gratuit</div>
              <div className="text-xs text-muted-foreground">Accès complet pendant 30 jours. Aucune carte requise pour commencer.</div>
            </div>
          </div>
        </div>

        <div className="max-w-md mx-auto">
          <div className="relative p-8 rounded-2xl bg-card border border-primary/40 ring-1 ring-primary/30 shadow-lg shadow-primary/5">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-primary text-white text-2xs font-bold px-4 py-1 rounded-full">1 MOIS GRATUIT</span>
            </div>
            <div className="text-center mb-6">
              <div className="text-xs font-bold tracking-widest text-primary mb-3">{PLAN.name}</div>
              <div className="flex items-baseline gap-1 justify-center mb-1">
                <span className="text-2xl font-bold text-positive line-through opacity-60 mr-1">$29</span>
                <span className="text-5xl font-bold text-foreground">$0</span>
                <span className="text-sm text-muted-foreground">/1er mois</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Puis $29/mois · Toutes les fonctionnalités incluses · Annulation à tout moment</p>
            </div>
            <ul className="space-y-2 mb-8 grid grid-cols-1 sm:grid-cols-2 gap-x-4">
              {PLAN.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-xs text-muted-foreground">
                  <Icon name="CheckIcon" size={14} className="text-positive mt-0.5 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <Link
              href="/onboarding"
              className="block w-full py-3 rounded-xl text-sm font-semibold text-center bg-primary text-white hover:bg-primary/90 transition-colors"
            >
              Commencer — 1 mois gratuit
            </Link>
            <p className="text-center text-xs text-muted-foreground mt-3">Aucune carte de crédit requise · Puis $29/mois après 30 jours</p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="text-2xl font-bold text-center text-foreground mb-10">Ce que disent nos utilisateurs</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="p-6 rounded-xl bg-card border border-border">
              <div className="flex gap-0.5 mb-3">
                {Array.from({ length: t.rating }).map((_, i) => (
                  <Icon key={i} name="StarIcon" size={14} className="text-warning" />
                ))}
              </div>
              <p className="text-sm text-foreground mb-4 leading-relaxed">"{t.text}"</p>
              <div>
                <div className="text-xs font-semibold text-foreground">{t.name}</div>
                <div className="text-xs text-muted-foreground">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Waitlist / Referral */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Waitlist */}
          <div className="p-8 rounded-2xl bg-gradient-to-br from-primary/10 to-purple-500/10 border border-primary/20">
            <h3 className="text-lg font-bold text-foreground mb-2">Rejoindre la liste d'attente</h3>
            <p className="text-sm text-muted-foreground mb-5">Accès prioritaire + 30 jours gratuits pour les 500 premiers inscrits.</p>
            {waitlistSubmitted ? (
              <div className="flex items-center gap-3 p-4 rounded-xl bg-positive-subtle border border-positive/20">
                <Icon name="CheckCircleIcon" size={20} className="text-positive" />
                <div>
                  <div className="text-sm font-semibold text-foreground">Vous êtes sur la liste !</div>
                  <div className="text-xs text-muted-foreground">Nous vous contacterons en priorité.</div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleWaitlist} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  className="flex-1 px-4 py-2.5 rounded-xl bg-background border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/40"
                  required
                />
                <button type="submit" className="px-5 py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors">
                  Rejoindre
                </button>
              </form>
            )}
          </div>

          {/* Referral */}
          <div className="p-8 rounded-2xl bg-card border border-border">
            <h3 className="text-lg font-bold text-foreground mb-2">Programme Ambassadeur</h3>
            <p className="text-sm text-muted-foreground mb-5">Parrainez des analystes et gagnez 1 mois gratuit par référence active.</p>
            <div className="p-4 rounded-xl bg-background border border-border mb-4">
              <div className="text-xs text-muted-foreground mb-1">Votre code de parrainage</div>
              <div className="flex items-center justify-between">
                <span className="font-mono-data text-lg text-primary font-bold">{referralCode}</span>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors"
                >
                  <Icon name={copied ? 'CheckIcon' : 'ClipboardIcon'} size={12} />
                  {copied ? 'Copié !' : 'Copier'}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Filleuls', value: '12' },
                { label: 'Mois Gagnés', value: '3' },
                { label: 'Valeur', value: '$87' },
              ].map((s) => (
                <div key={s.label} className="text-center p-3 rounded-xl bg-background border border-border">
                  <div className="text-xl font-bold font-mono-data text-primary">{s.value}</div>
                  <div className="text-2xs text-muted-foreground">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-4 py-16 text-center">
        <div className="p-12 rounded-2xl bg-gradient-to-br from-primary/10 via-background to-purple-500/10 border border-primary/20">
          <h2 className="text-3xl font-bold text-foreground mb-4">Prêt à analyser comme un pro ?</h2>
          <p className="text-muted-foreground mb-2">Rejoignez 5,000+ analystes qui utilisent NHL Analytics Pro chaque jour.</p>
          <p className="text-primary font-semibold text-lg mb-8">$29/mois · Tout inclus · Sans engagement</p>
          <Link href="/onboarding" className="inline-flex items-center gap-2 px-10 py-4 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors">
            <Icon name="RocketLaunchIcon" size={18} />
            Commencer maintenant
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <AppLogo size={20} />
            <span className="text-xs text-muted-foreground">NHL Analytics Pro © 2025</span>
          </div>
          <div className="flex gap-6">
            {[
              { label: 'Dashboard', href: '/' },
              { label: 'Transparence', href: '/trust-dashboard' },
              { label: 'Tarifs', href: '/launch#pricing' },
              { label: 'Onboarding', href: '/onboarding' },
            ].map((l) => (
              <Link key={l.label} href={l.href} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
